// Static client-facing translations (no AI needed) — fast, cheap, consistent.
// Resource descriptions/names still use cached AI translation; category headers
// and fixed UI labels use this dictionary so translation is instant and free.

export const SUPPORTED_LANGUAGES = {
  en: "English", es: "Español", ht: "Kreyòl Ayisyen",
  pt: "Português", ar: "العربية", cv: "Kriolu"
};

export const RTL_LANGUAGES = ["ar"];

// SDOH category headers translated into all 6 supported languages.
export const CATEGORY_TRANSLATIONS = {
  "Food Security": {
    en: "Food Security", es: "Seguridad Alimentaria", ht: "Sekirite Alimantè",
    pt: "Segurança Alimentar", ar: "الأمن الغذائي", cv: "Seguransa Alimentar"
  },
  "Housing & Shelter": {
    en: "Housing & Shelter", es: "Vivienda y Refugio", ht: "Lojman ak Abri",
    pt: "Habitação e Abrigo", ar: "السكن والمأوى", cv: "Kaza y Abrigu"
  },
  "Utilities": {
    en: "Utilities", es: "Servicios Públicos", ht: "Sèvis Piblik",
    pt: "Serviços Públicos", ar: "المرافق العامة", cv: "Servisus Publiku"
  },
  "Healthcare Access": {
    en: "Healthcare Access", es: "Acceso a Salud", ht: "Aksè nan Swen Sante",
    pt: "Acesso à Saúde", ar: "الوصول إلى الرعاية الصحية", cv: "Asesu a Saúdi"
  },
  "Mental Health": {
    en: "Mental Health", es: "Salud Mental", ht: "Sante Mantal",
    pt: "Saúde Mental", ar: "الصحة النفسية", cv: "Saúdi Mental"
  },
  "Substance Use": {
    en: "Substance Use", es: "Uso de Sustancias", ht: "Itilizasyon Sibstans",
    pt: "Uso de Substâncias", ar: "تعاطي المواد", cv: "Uzu di Substánsia"
  },
  "Legal / Immigration": {
    en: "Legal / Immigration", es: "Legal / Inmigración", ht: "Legal / Imigrasyon",
    pt: "Jurídico / Imigração", ar: "الشؤون القانونية / الهجرة", cv: "Legal / Imigrason"
  },
  "Employment / Income": {
    en: "Employment / Income", es: "Empleo / Ingresos", ht: "Travay / Revni",
    pt: "Emprego / Renda", ar: "التوظيف / الدخل", cv: "Empregu / Rendimentu"
  },
  "Transportation": {
    en: "Transportation", es: "Transporte", ht: "Transpò",
    pt: "Transporte", ar: "المواصلات", cv: "Transporti"
  },
  "Education / ESL": {
    en: "Education / ESL", es: "Educación / ESL", ht: "Edikasyon / ESL",
    pt: "Educação / ESL", ar: "التعليم / اللغة الإنجليزية", cv: "Edukason / ESL"
  },
  "Childcare / Family Support": {
    en: "Childcare / Family Support", es: "Cuidado Infantil / Apoyo Familiar",
    ht: "Gadri / Sipò Fanmi", pt: "Cuidado Infantil / Apoio Familiar",
    ar: "رعاية الأطفال / دعم الأسرة", cv: "Kuidadu di Kriansa / Apoiu Family"
  },
  "Violence / Safety": {
    en: "Violence / Safety", es: "Violencia / Seguridad", ht: "Vyolans / Sekirite",
    pt: "Violência / Segurança", ar: "العنف / السلامة", cv: "Violénsia / Seguransa"
  },
  "Digital Access": {
    en: "Digital Access", es: "Acceso Digital", ht: "Aksè Dijital",
    pt: "Acesso Digital", ar: "الوصول الرقمي", cv: "Asesu Dijital"
  }
};

// Fixed client-facing UI strings.
export const UI_STRINGS = {
  scan: {
    en: "Scan with your phone to view these resources digitally",
    es: "Escanea con tu teléfono para ver estos recursos digitalmente",
    ht: "Eskane ak telefòn ou pou wè resous sa yo an dijital",
    pt: "Escaneie com seu telefone para ver estes recursos digitalmente",
    ar: "امسح بهاتفك لعرض هذه الموارد رقميًا",
    cv: "Skaneia ku bu telefoni pa odja kes rekursu li dijitalmenti"
  },
  scanTitle: {
    en: "View this list on your phone", es: "Ver esta lista en tu teléfono",
    ht: "Wè lis sa a sou telefòn ou", pt: "Ver esta lista no seu telefone",
    ar: "اعرض هذه القائمة على هاتفك", cv: "Odja es lista na bu telefoni"
  },
  free: {
    en: "Free", es: "Gratis", ht: "Gratis", pt: "Gratuito", ar: "مجاني", cv: "Grátis"
  },
  download: {
    en: "Download PDF for Offline Access",
    es: "Descargar PDF para acceso sin conexión",
    ht: "Telechaje PDF pou aksè san entènèt",
    pt: "Baixar PDF para acesso offline",
    ar: "تحميل PDF للوصول بدون اتصال",
    cv: "Baixa PDF pa asesu offlini"
  },
  translate: {
    en: "Translate:", es: "Traducir:", ht: "Tradui:", pt: "Traduzir:", ar: "ترجمة:", cv: "Tradus:"
  },
  resources: {
    en: "resources", es: "recursos", ht: "resous", pt: "recursos", ar: "موارد", cv: "rekursus"
  },
  resource: {
    en: "resource", es: "recurso", ht: "resous", pt: "recurso", ar: "مورد", cv: "rekursu"
  },
  noResources: {
    en: "No resources found.", es: "No se encontraron recursos.",
    ht: "Pa gen resous ki jwenn.", pt: "Nenhum recurso encontrado.",
    ar: "لم يتم العثور على موارد.", cv: "Ka atxa nenhun rekursu."
  },
  listTitle: {
    en: "Your Personalized Resource List", es: "Tu Lista de Recursos Personalizada",
    ht: "Lis Resous Pèsonalize Ou", pt: "Sua Lista de Recursos Personalizada",
    ar: "قائمة الموارد المخصصة لك", cv: "Bu Lista di Rekursu Personalizadu"
  },
  hours: {
    en: "Hours", es: "Horario", ht: "Lè", pt: "Horário", ar: "ساعات العمل", cv: "Oras"
  },
  phone: {
    en: "Phone", es: "Teléfono", ht: "Telefòn", pt: "Telefone", ar: "الهاتف", cv: "Telefoni"
  },
  address: {
    en: "Address", es: "Dirección", ht: "Adrès", pt: "Endereço", ar: "العنوان", cv: "Morada"
  },
  directions: {
    en: "Get Directions", es: "Cómo Llegar", ht: "Jwenn Direksyon",
    pt: "Como Chegar", ar: "احصل على الاتجاهات", cv: "Odja Kaminhu"
  },
  visitWebsite: {
    en: "Visit Website", es: "Visitar Sitio Web", ht: "Vizite Sit Entènèt",
    pt: "Visitar Site", ar: "زيارة الموقع", cv: "Vizita Sítiu Web"
  },
  shareThisList: {
    en: "Share this list", es: "Compartir esta lista", ht: "Pataje lis sa a",
    pt: "Compartilhar esta lista", ar: "شارك هذه القائمة", cv: "Partilha es lista"
  },
  shareVia: {
    en: "Share via", es: "Compartir por", ht: "Pataje ak",
    pt: "Compartilhar via", ar: "المشاركة عبر", cv: "Partilha atraves di"
  },
  shareNative: {
    en: "Phone's share menu", es: "Menú de compartir del teléfono", ht: "Meni pataje telefòn lan",
    pt: "Menu de compartilhar do telefone", ar: "قائمة المشاركة في الهاتف", cv: "Menu di partilha di telefoni"
  },
  shareSms: {
    en: "Text message", es: "Mensaje de texto", ht: "Mesaj tèks",
    pt: "Mensagem de texto", ar: "رسالة نصية", cv: "Mensajen di tekstu"
  },
  shareEmail: {
    en: "Email", es: "Correo electrónico", ht: "Imèl", pt: "E-mail", ar: "البريد الإلكتروني", cv: "Email"
  },
  shareCopy: {
    en: "Copy message", es: "Copiar mensaje", ht: "Kopye mesaj la",
    pt: "Copiar mensagem", ar: "نسخ الرسالة", cv: "Kopia mensajen"
  },
  shareCopied: {
    en: "Copied!", es: "¡Copiado!", ht: "Kopye!", pt: "Copiado!", ar: "تم النسخ!", cv: "Kopiadu!"
  },
  shareSubject: {
    en: "Your resources from 617Reach", es: "Sus recursos de 617Reach",
    ht: "Resous ou yo nan 617Reach", pt: "Seus recursos da 617Reach",
    ar: "مواردك من 617Reach", cv: "Bus rekursus di 617Reach"
  }
};

export const getCategoryLabel = (category, lang) => {
  if (!category) return category;
  return CATEGORY_TRANSLATIONS[category]?.[lang] || category;
};

export const t = (key, lang) => {
  return UI_STRINGS[key]?.[lang] || UI_STRINGS[key]?.en || key;
};

export const isRTL = (lang) => RTL_LANGUAGES.includes(lang);
